import { useEffect, useMemo, useRef } from 'react'
import { useAppStore } from '@core/store'
import { downloadBlob } from '@core/lib/downloadBlob'
import { useAuth } from '@core/auth/useAuth'
import type { LoadedModel } from '@viewer/store/viewerSlice'
import { parseIds, type IdsFile } from './lib/idsParse'
import { idsDeFolha } from './lib/idsDeFolha'
import { lerFolhas } from '@core/lib/folhas'
import { idsPaintOff, idsPaintOn } from './lib/idsPaint'
import { useIdsModels } from './useIdsModels'
import {
  evaluateIds,
  buildIdsColorMap,
  buildBcfTopicDrafts,
  idsToCsv,
  type IdsReport,
} from './lib/idsEngine'

// Núcleo partilhado da validação IDS. Encapsula todo o estado e as ações
// (abrir .ids, validar, exportar CSV/BCF, pintar no 3D) para que o painel
// flutuante (IdsPanel, atalho do ribbon) e a página do módulo (IdsWorkspace
// Page) reutilizem exatamente a mesma lógica em vez de a duplicarem.
//
// O estado NÃO é local: vive no store (`store/idsModuleSlice.ts`). Duas
// razões, ambas bugs do Rui. Primeiro, a casca do workspace desmonta a
// página do módulo ao sair dela e com `useState` isso deitava fora o
// relatório inteiro, obrigando a revalidar. Segundo, com estado local o
// painel e a página eram duas instâncias independentes: validar num
// deixava o outro em branco, sobre o mesmo modelo e o mesmo .ids.
//
// A componente visual do relatório (barra-resumo + lista de specs com falhas
// clicáveis) vive em IdsResults.tsx, também partilhada.

export interface IdsTotals {
  ok: number
  failing: number
  unresolved: number
}

export interface UseIdsValidation {
  /** Modelos IFC carregados no viewer (fonte para o seletor). */
  models: LoadedModel[]
  /** Modelo efetivo a validar (escolha do utilizador se válida, senão o
   *  ativo, senão o primeiro). */
  modelId: string
  setModelSel: (id: string) => void
  ids: IdsFile | null
  idsName: string
  report: IdsReport | null
  /** Modelo que produziu o relatório. É este que a lista de specs usa
   *  para seleccionar elementos, não o do seletor. */
  reportModelId: string
  busy: boolean
  step: string | null
  error: string | null
  show3d: boolean
  setShow3d: (on: boolean) => void
  bcfMsg: string | null
  /** BCF disponível neste workspace (para mostrar "Exportar para BCF"). */
  bcfEnabled: boolean
  expanded: Set<number>
  toggleSpec: (i: number) => void
  totals: IdsTotals | null
  /** Os modelos mudaram desde a última validação: o relatório foi limpo
   *  e há que avisar em vez de deixar o ecrã vazio sem explicação. */
  stale: boolean
  dismissStale: () => void
  /** Lê e faz parse de um ficheiro .ids; em erro limpa o IDS e reporta. */
  loadIdsFile: (file: File) => Promise<void>
  /** Adota um IDS já em texto (usado pelo pré-preenchimento por deep link). */
  adoptIds: (xml: string, name: string) => void
  /** Valida o IDS carregado contra o modelo escolhido. O `override`
   *  permite validar um IDS que ainda não passou pelo estado (é o caso do
   *  editor de autoria, que valida o que está a ser editado sem esperar
   *  por um ciclo de render); passa também a ser o IDS corrente. */
  validate: (override?: { ids: IdsFile; name?: string }) => Promise<void>
  exportCsv: () => void
  exportBcf: () => void
}

/**
 * @param active Se o relatório deve estar pintado no 3D. No painel flutuante
 *   corresponde ao "aberto"; na página é `true` enquanto está montada.
 */
export function useIdsValidation(opts: { active: boolean }): UseIdsValidation {
  const { mode, modules, user } = useAuth()
  const bcfEnabled = mode === 'cloud' && modules.includes('bcf')

  const modelSel = useAppStore((s) => s.idsModelSel)
  const { models, modelId } = useIdsModels(modelSel)
  const setModelSel = useAppStore((s) => s.setIdsModelSel)

  const ids = useAppStore((s) => s.idsFile)
  const idsName = useAppStore((s) => s.idsFileName)
  const report = useAppStore((s) => s.idsReport)
  const reportModelId = useAppStore((s) => s.idsReportModelId)
  const busy = useAppStore((s) => s.idsBusy)
  const step = useAppStore((s) => s.idsStep)
  const error = useAppStore((s) => s.idsError)
  const show3d = useAppStore((s) => s.idsShow3d)
  const setShow3d = useAppStore((s) => s.setIdsShow3d)
  const bcfMsg = useAppStore((s) => s.idsBcfMsg)
  const expanded = useAppStore((s) => s.idsExpandedSpecs)
  const toggleSpec = useAppStore((s) => s.toggleIdsSpec)
  const stale = useAppStore((s) => s.idsStale)
  const dismissStale = useAppStore((s) => s.dismissIdsStale)
  const setIdsFile = useAppStore((s) => s.setIdsFile)
  const setError = useAppStore((s) => s.setIdsError)
  const setBcfMsg = useAppStore((s) => s.setIdsBcfMsg)

  // Identidade desta superfície no registo de pintores (ver idsPaint):
  // o painel e a página partilham o relatório e podem estar montados ao
  // mesmo tempo, por isso quem desmonta não pode despintar por todos.
  const token = useMemo(() => ({}), [])
  const paintedRef = useRef(false)

  // Pintar / despintar o relatório no 3D. Mesma camada de cores do diff
  // (applyDiffColors): verde passa, vermelho falha.
  useEffect(() => {
    if (opts.active && report && show3d && reportModelId) {
      void idsPaintOn(token, buildIdsColorMap(report, reportModelId))
      paintedRef.current = true
    } else if (paintedRef.current) {
      void idsPaintOff(token)
      paintedRef.current = false
    }
  }, [opts.active, report, show3d, reportModelId, token])

  useEffect(
    () => () => {
      if (paintedRef.current) void idsPaintOff(token)
    },
    [token],
  )

  const adoptIds = (xml: string, name: string) => {
    try {
      setIdsFile(parseIds(xml), name)
    } catch (err) {
      setIdsFile(null, '')
      setError(err instanceof Error ? err.message : 'Falha a ler o .ids.')
    }
  }

  const loadIdsFile = async (file: File) => {
    try {
      // Uma folha de requisitos (.xlsx ou .csv) converte-se em IDS e
      // valida-se igual; as linhas que não se perceberam vão num aviso.
      if (/\.(xlsx|csv)$/i.test(file.name)) {
        const [folha] = lerFolhas(new Uint8Array(await file.arrayBuffer()), file.name)
        const { ids: convertido, avisos } = idsDeFolha(folha?.linhas ?? [], file.name.replace(/\.[^.]+$/, ''))
        setIdsFile(convertido, file.name)
        if (avisos.length > 0) {
          useAppStore.getState().pushToast(
            `${avisos.length} linha(s) da folha ficaram de fora (ex.: linha ${avisos[0].linha}: ${avisos[0].motivo}). Abre-a no editor de IDS para ver todas.`,
            { type: 'info' },
          )
        }
        return
      }
      const text = await file.text()
      setIdsFile(parseIds(text), file.name)
    } catch (err) {
      setIdsFile(null, '')
      setError(err instanceof Error ? err.message : 'Falha a ler o .ids.')
    }
  }

  const validate = async (override?: { ids: IdsFile; name?: string }) => {
    const target = override?.ids ?? ids
    if (!target || !modelId) return
    if (override) setIdsFile(override.ids, override.name ?? '')
    const st = useAppStore.getState()
    // Assinatura dos modelos à partida: se entretanto trocarem de IFC, o
    // resultado que vier a caminho é de um modelo que já não está
    // carregado e não pode aterrar no ecrã.
    const keyAtStart = st.idsModelKey
    st.startIdsValidation()
    try {
      const r = await evaluateIds(modelId, target, (s) =>
        useAppStore.getState().setIdsStep(s),
      )
      if (useAppStore.getState().idsModelKey !== keyAtStart) return
      useAppStore.getState().finishIdsValidation(r, modelId)
    } catch (err) {
      if (useAppStore.getState().idsModelKey !== keyAtStart) return
      useAppStore
        .getState()
        .failIdsValidation(
          err instanceof Error ? err.message : 'Falha a validar.',
        )
    }
  }

  const exportCsv = () => {
    if (!report) return
    const outName = `${idsName.replace(/\.ids$/i, '') || 'ids'}_relatorio.csv`
    downloadBlob('﻿' + idsToCsv(report), outName, 'text/csv;charset=utf-8')
    useAppStore.getState().pushToast(`Relatório IDS exportado (${outName})`, {
      type: 'success',
    })
  }

  const exportBcf = () => {
    if (!report) return
    const drafts = buildBcfTopicDrafts(report)
    if (drafts.length === 0) {
      setBcfMsg('Sem falhas para exportar.')
      return
    }
    const st = useAppStore.getState()
    const bcf = st.bcfCreateEmpty(
      `IDS: ${idsName.replace(/\.ids$/i, '') || 'validacao'}`,
    )
    const author = user?.email ?? null
    // O bcfCreateTopic defaulta os campos em falta em runtime, mas o tipo do
    // draft exige-os todos; passamos nulos/vazios (o creationAuthor/creation
    // Date são sobrescritos pela store).
    for (const d of drafts) {
      st.bcfCreateTopic(
        bcf.localId,
        {
          title: d.title,
          description: d.description,
          status: null,
          priority: null,
          type: null,
          assignedTo: null,
          creationAuthor: author,
          creationDate: new Date().toISOString(),
          modifiedAuthor: null,
          modifiedDate: null,
          dueDate: null,
          stage: null,
          labels: ['IDS'],
        },
        author,
      )
    }
    setBcfMsg(
      `${drafts.length} tópico${drafts.length === 1 ? '' : 's'} criado${drafts.length === 1 ? '' : 's'} no BCF "${bcf.name}". Abre o módulo BCF para os ver e capturar viewpoints.`,
    )
  }

  const totals: IdsTotals | null = report
    ? {
        ok: report.results.filter((r) => r.status === 'ok').length,
        failing: report.results.filter((r) => r.status === 'has-failures')
          .length,
        unresolved: report.results.filter((r) => r.status === 'unresolved')
          .length,
      }
    : null

  return {
    models,
    modelId,
    setModelSel,
    ids,
    idsName,
    report,
    reportModelId,
    busy,
    step,
    error,
    show3d,
    setShow3d,
    bcfMsg,
    bcfEnabled,
    expanded,
    toggleSpec,
    totals,
    stale,
    dismissStale,
    loadIdsFile,
    adoptIds,
    validate,
    exportCsv,
    exportBcf,
  }
}
